import React from "react";

import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Button,
  Stack,
  IconButton,
  FormHelperText,
} from "@mui/material";

import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

function ComplaintDetailsStep({
  complaintType,
  selectedItemData,
  productionDate,
  onProductionDateChange,
  lotNumber,
  expirationDate,
  quantity,
  quantityType,
  description,
  files,
  onLotNumberChange,
  onExpirationDateChange,
  onQuantityChange,
  onQuantityTypeChange,
  onDescriptionChange,
  onFilesChange,
  onRemoveFile,
  showValidation = false,
  t,
}) {
  const isProductComplaint = complaintType === "product";

  // =========================================================
  // DYNAMIC FIELDS
  // =========================================================

  const showBatchNumber = selectedItemData?.plainteInfo === 1;

  const showProductionDate = selectedItemData?.plainteInfo === 2;

  const showExpirationDate = selectedItemData?.plainteInfo === 4;

  // =========================================================
  // VALIDATION
  // =========================================================

  const lotInvalid =
    isProductComplaint &&
    showValidation &&
    showBatchNumber &&
    !lotNumber?.trim();

  const productionDateInvalid =
    isProductComplaint &&
    showValidation &&
    showProductionDate &&
    !productionDate;

  const expirationDateInvalid =
    isProductComplaint &&
    showValidation &&
    showExpirationDate &&
    !expirationDate;

  const quantityInvalid =
    isProductComplaint &&
    showValidation &&
    (!quantity || Number(quantity) <= 0);

  const quantityTypeInvalid =
    isProductComplaint && showValidation && !quantityType;

  const descriptionInvalid = showValidation && !description?.trim();

  const picturesInvalid =
    isProductComplaint && showValidation && (!files || files.length === 0);

  // =========================================================
  // FILES
  // =========================================================

  const handleFileChange = (event) => {
    if (!event.target.files) {
      return;
    }

    const newFiles = Array.from(event.target.files);

    onFilesChange(newFiles);

    event.target.value = "";
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box>
      {/* =====================================================
          TITLE
      ===================================================== */}

      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        {t("ComplaintDetails")}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t("EnterComplaintDetails")}
      </Typography>

      {/* =====================================================
          PRODUCT INFORMATION
      ===================================================== */}

      {isProductComplaint && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
            {t("ProductInformation")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            {/* LOT */}

            {showBatchNumber && (
              <TextField
                fullWidth
                required
                error={lotInvalid}
                label={t("LotNumber")}
                value={lotNumber}
                onChange={(event) => onLotNumberChange(event.target.value)}
                helperText={lotInvalid ? t("LotNumberRequired") : ""}
                InputProps={{
                  startAdornment: (
                    <NumbersOutlinedIcon
                      color={lotInvalid ? "error" : "action"}
                      sx={{
                        mr: 1,
                      }}
                    />
                  ),
                }}
              />
            )}

            {/* PRODUCTION DATE */}

            {showProductionDate && (
              <TextField
                fullWidth
                required
                error={productionDateInvalid}
                type="date"
                label={t("ProductionDate")}
                value={productionDate}
                onChange={(event) => onProductionDateChange(event.target.value)}
                helperText={
                  productionDateInvalid ? t("ProductionDateRequired") : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <EventOutlinedIcon
                      color={productionDateInvalid ? "error" : "action"}
                      sx={{
                        mr: 1,
                      }}
                    />
                  ),
                }}
              />
            )}

            {/* EXPIRATION DATE */}

            {showExpirationDate && (
              <TextField
                fullWidth
                required
                error={expirationDateInvalid}
                type="date"
                label={t("ExpirationDate")}
                value={expirationDate}
                onChange={(event) => onExpirationDateChange(event.target.value)}
                helperText={
                  expirationDateInvalid ? t("ExpirationDateRequired") : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <EventOutlinedIcon
                      color={expirationDateInvalid ? "error" : "action"}
                      sx={{
                        mr: 1,
                      }}
                    />
                  ),
                }}
              />
            )}

            {/* QUANTITY */}

            <TextField
              fullWidth
              required
              error={quantityInvalid}
              type="number"
              label={t("Quantity")}
              value={quantity}
              onChange={(event) => onQuantityChange(event.target.value)}
              helperText={quantityInvalid ? t("QuantityRequired") : ""}
              inputProps={{
                min: 1,
              }}
              InputProps={{
                startAdornment: (
                  <NumbersOutlinedIcon
                    color={quantityInvalid ? "error" : "action"}
                    sx={{
                      mr: 1,
                    }}
                  />
                ),
              }}
            />

            {/* UNIT / PACK */}

            <FormControl fullWidth required error={quantityTypeInvalid}>
              <InputLabel>{t("PackUnit")}</InputLabel>

              <Select
                value={quantityType}
                label={t("PackUnit")}
                onChange={(event) => onQuantityTypeChange(event.target.value)}
              >
                <MenuItem value="unit">{t("Unit")}</MenuItem>

                <MenuItem value="pack">{t("Pack")}</MenuItem>
              </Select>

              {quantityTypeInvalid && (
                <FormHelperText>{t("QuantityTypeRequired")}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Paper>
      )}

      {/* =====================================================
          DESCRIPTION
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          {t("ComplaintDescription")}
        </Typography>

        <TextField
          fullWidth
          required
          error={descriptionInvalid}
          multiline
          minRows={5}
          label={t("Description")}
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          placeholder={t("DescribeComplaint")}
          helperText={descriptionInvalid ? t("DescriptionRequired") : ""}
          InputProps={{
            startAdornment: (
              <DescriptionOutlinedIcon
                color={descriptionInvalid ? "error" : "action"}
                sx={{
                  mr: 1,
                  mt: 1.5,
                  alignSelf: "flex-start",
                }}
              />
            ),
          }}
        />
      </Paper>

      {/* =====================================================
          PICTURES
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2,
          borderColor: picturesInvalid ? "error.main" : "divider",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            mb: 0.5,
            color: picturesInvalid ? "error.main" : "text.primary",
          }}
        >
          {t("Pictures")}
          {isProductComplaint && " *"}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isProductComplaint
            ? t("AtLeastOnePictureRequired")
            : t("PicturesOptional")}
        </Typography>

        <Button
          component="label"
          variant="outlined"
          color={picturesInvalid ? "error" : "primary"}
          startIcon={<CloudUploadOutlinedIcon />}
        >
          {t("AddPictures")}

          <input
            hidden
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
          />
        </Button>

        {picturesInvalid && (
          <Typography
            variant="caption"
            color="error"
            display="block"
            sx={{ mt: 1 }}
          >
            {t("PicturesRequired")}
          </Typography>
        )}

        {/* FILE PREVIEWS */}

        {files?.length > 0 && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mt: 3,
              flexWrap: "wrap",
              rowGap: 2,
            }}
          >
            {files.map((file, index) => {
              const fileURL = URL.createObjectURL(file);

              return (
                <Box
                  key={`${file.name}-${index}`}
                  sx={{
                    position: "relative",
                    width: 110,
                    height: 110,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    component="img"
                    src={fileURL}
                    alt={file.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  <IconButton
                    size="small"
                    onClick={() => onRemoveFile(index)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255,255,255,0.9)",
                      "&:hover": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

export default ComplaintDetailsStep;
